import PersonPinRounded from '@mui/icons-material/PersonPinRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useState } from 'react';

import { RecipientAutoDetectionStatus, User, UserMin } from '@apiContract';
import { Iconify } from '@components/Iconify';
import { useUserLocation } from '@hooks/useUserLocation';

import { useAssignmentRecipient } from '@components/Capture/hooks/useAssignmentRecipient';
import { useUpdateQueue } from '@components/Capture/hooks/useUpdateQueue';
import { CapturePiece } from '@components/Capture/types';
import { useRoutesByLocation } from '@hooks/useRoutesByLocation';
import { getRoutesList } from '@utils/mappers';

import { mdNotification, sxIcon, sxNotification, sxNotificationAction, sxNotificationText } from './styles';

type Props = {
  recipient?: User | UserMin;
  capturePiece?: CapturePiece | null;
};

const RecipientNotification: FC<Props> = ({ recipient, capturePiece }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { location, isLoading: isLocationResponseLoading } = useUserLocation(recipient);
  const routes = useRoutesByLocation(location?.id);
  const { addEvent } = useUpdateQueue({ context: 'MailPiece' });
  const { assignRecipientToPiece } = useAssignmentRecipient(addEvent);
  const pieceStatus =
    capturePiece && (capturePiece.piece?.recipientAutoDetection?.status || capturePiece.pieceDetection?.status);
  const pieceRecipient = capturePiece && !capturePiece.piece?.recipient && capturePiece.pieceDetection?.recipient;

  const isConfirm =
    !!recipient &&
    !!capturePiece?.piece &&
    pieceStatus &&
    pieceRecipient &&
    [
      RecipientAutoDetectionStatus.Identified,
      RecipientAutoDetectionStatus.IdentifiedBasedOnPreviousAssignment,
      RecipientAutoDetectionStatus.IdentifiedVerificationRequired,
    ].includes(pieceStatus);

  const onConfirm = () => {
    setIsLoading(true);
    if (isConfirm && capturePiece?.piece) {
      assignRecipientToPiece(recipient, capturePiece.piece);
    }
  };

  useEffect(() => {
    if (recipient) {
      setIsVisible(true);
      setIsLoading(false);
    }
  }, [recipient]);

  const onClose = () => {
    setIsVisible(false);
  };

  const color = isConfirm ? 'warning' : 'success';

  return (
    isVisible && (
      <Box sx={isMobile ? sxNotification : mdNotification}>
        <Box sx={sxIcon(color)}>
          <PersonPinRounded color={color} />
        </Box>
        <Box sx={sxNotificationText}>
          <Typography variant="body2">
            {recipient?.displayName}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({recipient?.userName})
            </Typography>
          </Typography>
          {location &&
            (routes.isLoading || isLocationResponseLoading ? (
              <Skeleton variant="text" width="40%" />
            ) : (
              <Box sx={{ display: 'flex' }}>
                <Iconify
                  icon={'material-symbols:mode-of-travel'}
                  width={16}
                  height={16}
                  color={theme.palette.text.secondary}
                  sx={{ m: '0 4px 0 0' }}
                />
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {getRoutesList(routes?.data)}
                  {location?.name}
                </Typography>
              </Box>
            ))}
        </Box>
        <Box sx={sxNotificationAction}>
          {isConfirm ? (
            <LoadingButton
              variant="soft"
              size="small"
              color="warning"
              onClick={onConfirm}
              loading={isLoading}
              sx={{ mr: 1, ml: 1 }}
            >
              Confirm
            </LoadingButton>
          ) : (
            <IconButton size="small" onClick={onClose} sx={{ p: 0.5, mr: 0.5 }}>
              <Iconify width={16} icon="mingcute:close-line" />
            </IconButton>
          )}
        </Box>
      </Box>
    )
  );
};

export default RecipientNotification;
